import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  Timestamp,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";

/* ═══════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════ */

export interface GlamourProductReview {
  guestId:     string;
  guestName?:  string;
  guestAvatar?: string;
  orderId:     string;
  listingId:   string;
  brandId?:    string;
  hostId?:     string;

  // Core rating
  rating:       number; // 1–5
  reviewText:   string;
  title?:       string;

  // Detailed criteria (1–5 each)
  quality:       number;
  deliverySpeed: number;
  accuracy:      number;
  communication: number;
  value:         number;

  // Fashion-specific flags
  trueToSize?:     boolean;
  fitFeedback?:    "runs_small" | "true_to_size" | "runs_large";
  damaged?:        boolean;
  wouldRecommend?: boolean;

  // Media
  images?: string[];

  // Moderation
  isApproved?:  boolean;
  isFlagged?:   boolean;
  flagReason?:  string;

  dateCreated: string;
  updatedAt?:  string;
}

export interface GlamourRatingSummary {
  // Averages
  avgRating:     number;
  quality:       number;
  deliverySpeed: number;
  accuracy:      number;
  communication: number;
  value:         number;

  // Distribution  (how many gave 1★, 2★ ... 5★)
  distribution: {
    one:   number;
    two:   number;
    three: number;
    four:  number;
    five:  number;
  };

  // Fashion specifics
  trueToSizePercentage: number;
  fitBreakdown: {
    runs_small:    number;
    true_to_size:  number;
    runs_large:    number;
  };

  recommendPercentage: number;
  damageReports:       number;

  totalReviews: number;
  lastUpdated:  string;
}

export interface GlamourBrandRatingSummary {
  brandId:       string;
  avgRating:     number;
  totalReviews:  number;
  quality:       number;
  deliverySpeed: number;
  communication: number;
  value:         number;
  recommendPercentage: number;
  lastUpdated:   string;
}

interface GlamourRatingState {
  // Per-listing
  reviews:   Record<string, GlamourProductReview[]>;
  summaries: Record<string, GlamourRatingSummary | null>;

  // Per-brand
  brandSummaries: Record<string, GlamourBrandRatingSummary | null>;

  // Checked whether a specific order is already reviewed
  reviewedOrders: Record<string, boolean>; // key: `${listingId}_${orderId}`

  loading: boolean;
  error:   string | null;
}

const initialState: GlamourRatingState = {
  reviews:        {},
  summaries:      {},
  brandSummaries: {},
  reviewedOrders: {},
  loading:        false,
  error:          null,
};

const COLLECTION = "glamourRatings"; // 🔑 Glamour collection name

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */

const avg = (current: number, n: number, next: number) =>
  (current * n + next) / (n + 1);

const buildDefaultSummary = (): GlamourRatingSummary => ({
  avgRating:     0,
  quality:       0,
  deliverySpeed: 0,
  accuracy:      0,
  communication: 0,
  value:         0,
  distribution:  { one: 0, two: 0, three: 0, four: 0, five: 0 },
  trueToSizePercentage: 0,
  fitBreakdown:  { runs_small: 0, true_to_size: 0, runs_large: 0 },
  recommendPercentage:  0,
  damageReports: 0,
  totalReviews:  0,
  lastUpdated:   new Date().toISOString(),
});

const DIST_MAP: Record<number, keyof GlamourRatingSummary["distribution"]> = {
  1: "one", 2: "two", 3: "three", 4: "four", 5: "five",
};

/* ═══════════════════════════════════════════════════════════
   SUBMIT PRODUCT REVIEW
═══════════════════════════════════════════════════════════ */

export const submitGlamourProductReview = createAsyncThunk<
  { listingId: string; review: GlamourProductReview; summary: GlamourRatingSummary },
  {
    listingId:    string;
    hostId:       string;
    brandId?:     string;
    guestId:      string;
    guestName?:   string;
    guestAvatar?: string;
    orderId:      string;
    rating:       number;
    title?:       string;
    reviewText:   string;
    criteria: {
      quality:       number;
      deliverySpeed: number;
      accuracy:      number;
      communication: number;
      value:         number;
    };
    trueToSize?:     boolean;
    fitFeedback?:    "runs_small" | "true_to_size" | "runs_large";
    damaged?:        boolean;
    wouldRecommend?: boolean;
    images?:         string[];
  },
  { rejectValue: string }
>(
  "glamourRatings/submitProductReview",
  async (
    {
      listingId,
      hostId,
      brandId,
      guestId,
      guestName,
      guestAvatar,
      orderId,
      rating,
      title,
      reviewText,
      criteria,
      trueToSize,
      fitFeedback,
      damaged,
      wouldRecommend,
      images,
    },
    { rejectWithValue }
  ) => {
    try {
      /* ── 1. Guard: one review per order per listing ── */
      const guestRef = doc(db, COLLECTION, listingId, "reviews", guestId);
      const guestSnap = await getDoc(guestRef);

      const newReview: GlamourProductReview = {
        guestId,
        guestName:   guestName   ?? "",
        guestAvatar: guestAvatar ?? "",
        orderId,
        listingId,
        brandId:     brandId ?? "",
        hostId:      hostId,
        rating:      Math.min(5, Math.max(1, Math.round(rating))),
        title:       title       ?? "",
        reviewText,
        quality:       criteria.quality,
        deliverySpeed: criteria.deliverySpeed,
        accuracy:      criteria.accuracy,
        communication: criteria.communication,
        value:         criteria.value,
        trueToSize,
        fitFeedback,
        damaged,
        wouldRecommend,
        images:      images ?? [],
        isApproved:  true,
        isFlagged:   false,
        dateCreated: new Date().toISOString(),
      };

      if (guestSnap.exists()) {
        const existing = guestSnap.data();
        const reviews: GlamourProductReview[] = existing.reviews || [];

        if (reviews.some((r) => r.orderId === orderId)) {
          // Already reviewed — return current summary without throwing
          const summarySnap = await getDoc(doc(db, COLLECTION, listingId));
          const summary: GlamourRatingSummary =
            summarySnap.data()?.summary ?? buildDefaultSummary();
          return { listingId, review: newReview, summary };
        }

        await updateDoc(guestRef, {
          reviews:     [...reviews, newReview],
          lastUpdated: Timestamp.now(),
        });
      } else {
        await setDoc(guestRef, {
          guestId,
          reviews:   [newReview],
          createdAt: Timestamp.now(),
        });
      }

      /* ── 2. Update listing summary ── */
      const listingRef  = doc(db, COLLECTION, listingId);
      const listingSnap = await getDoc(listingRef);

      const summary: GlamourRatingSummary =
        listingSnap.data()?.summary ?? buildDefaultSummary();

      const n    = summary.totalReviews;
      const dist = { ...summary.distribution };
      const fitB = { ...summary.fitBreakdown };

      // Increment star bucket
      const bucket = DIST_MAP[Math.round(newReview.rating)];
      if (bucket) dist[bucket] += 1;

      // Fit feedback
      if (fitFeedback) fitB[fitFeedback] += 1;

      const updatedSummary: GlamourRatingSummary = {
        avgRating:     avg(summary.avgRating,     n, rating),
        quality:       avg(summary.quality,       n, criteria.quality),
        deliverySpeed: avg(summary.deliverySpeed, n, criteria.deliverySpeed),
        accuracy:      avg(summary.accuracy,      n, criteria.accuracy),
        communication: avg(summary.communication, n, criteria.communication),
        value:         avg(summary.value,         n, criteria.value),

        distribution: dist,

        trueToSizePercentage:
          avg(summary.trueToSizePercentage, n, trueToSize ? 100 : 0),

        fitBreakdown: fitB,

        recommendPercentage:
          avg(summary.recommendPercentage, n, wouldRecommend ? 100 : 0),

        damageReports: summary.damageReports + (damaged ? 1 : 0),

        totalReviews: n + 1,
        lastUpdated:  new Date().toISOString(),
      };

      if (listingSnap.exists()) {
        await updateDoc(listingRef, { summary: updatedSummary });
      } else {
        await setDoc(listingRef, {
          listingId,
          hostId,
          brandId: brandId ?? "",
          summary: updatedSummary,
          createdAt: Timestamp.now(),
        });
      }

      /* ── 3. Update brand aggregate summary ── */
      if (brandId) {
        const brandRef  = doc(db, "glamourBrandRatings", brandId);
        const brandSnap = await getDoc(brandRef);

        const bSummary: GlamourBrandRatingSummary = brandSnap.exists()
          ? (brandSnap.data() as GlamourBrandRatingSummary)
          : {
              brandId,
              avgRating:     0,
              totalReviews:  0,
              quality:       0,
              deliverySpeed: 0,
              communication: 0,
              value:         0,
              recommendPercentage: 0,
              lastUpdated:   new Date().toISOString(),
            };

        const bn = bSummary.totalReviews;

        const updatedBrand: GlamourBrandRatingSummary = {
          ...bSummary,
          avgRating:     avg(bSummary.avgRating,     bn, rating),
          quality:       avg(bSummary.quality,       bn, criteria.quality),
          deliverySpeed: avg(bSummary.deliverySpeed, bn, criteria.deliverySpeed),
          communication: avg(bSummary.communication, bn, criteria.communication),
          value:         avg(bSummary.value,         bn, criteria.value),
          recommendPercentage:
            avg(bSummary.recommendPercentage, bn, wouldRecommend ? 100 : 0),
          totalReviews: bn + 1,
          lastUpdated:  new Date().toISOString(),
        };

        if (brandSnap.exists()) {
          await updateDoc(brandRef, updatedBrand as Record<string, any>);
        } else {
          await setDoc(brandRef, updatedBrand);
        }
      }

      return { listingId, review: newReview, summary: updatedSummary };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to submit review");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   FETCH ALL REVIEWS FOR A LISTING
═══════════════════════════════════════════════════════════ */

export const fetchGlamourReviewsByListing = createAsyncThunk<
  { listingId: string; reviews: GlamourProductReview[] },
  string,
  { rejectValue: string }
>(
  "glamourRatings/fetchByListing",
  async (listingId, { rejectWithValue }) => {
    try {
      const colRef = collection(db, COLLECTION, listingId, "reviews");
      const snap   = await getDocs(colRef);

      const all: GlamourProductReview[] = [];
      snap.forEach((d) => {
        const data = d.data();
        if (Array.isArray(data.reviews)) all.push(...data.reviews);
      });

      // Sort newest first
      all.sort(
        (a, b) =>
          new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
      );

      return { listingId, reviews: all };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch reviews");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   FETCH REVIEW FOR A SPECIFIC ORDER  (check if already reviewed)
═══════════════════════════════════════════════════════════ */

export const fetchGlamourReviewByOrder = createAsyncThunk<
  GlamourProductReview | null,
  { listingId: string; orderId: string; guestId: string },
  { rejectValue: string }
>(
  "glamourRatings/fetchByOrder",
  async ({ listingId, orderId, guestId }, { rejectWithValue }) => {
    try {
      const docRef = doc(db, COLLECTION, listingId, "reviews", guestId);
      const snap   = await getDoc(docRef);

      if (!snap.exists()) return null;

      const reviews: GlamourProductReview[] = snap.data().reviews ?? [];
      return reviews.find((r) => r.orderId === orderId) ?? null;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to check review");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   FETCH LISTING SUMMARY ONLY  (display stars without all reviews)
═══════════════════════════════════════════════════════════ */

export const fetchGlamourListingSummary = createAsyncThunk<
  { listingId: string; summary: GlamourRatingSummary | null },
  string,
  { rejectValue: string }
>(
  "glamourRatings/fetchListingSummary",
  async (listingId, { rejectWithValue }) => {
    try {
      const snap = await getDoc(doc(db, COLLECTION, listingId));
      return {
        listingId,
        summary: snap.exists() ? (snap.data().summary ?? null) : null,
      };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch summary");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   FETCH ALL SUMMARIES  (bulk — product grid / PLP)
═══════════════════════════════════════════════════════════ */

export const fetchAllGlamourSummaries = createAsyncThunk<
  Record<string, GlamourRatingSummary | null>,
  void,
  { rejectValue: string }
>(
  "glamourRatings/fetchAllSummaries",
  async (_, { rejectWithValue }) => {
    try {
      const snap = await getDocs(collection(db, COLLECTION));
      const summaries: Record<string, GlamourRatingSummary | null> = {};
      snap.forEach((d) => {
        summaries[d.id] = d.data().summary ?? null;
      });
      return summaries;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch all summaries");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   FETCH BRAND SUMMARY
═══════════════════════════════════════════════════════════ */

export const fetchGlamourBrandSummary = createAsyncThunk<
  { brandId: string; summary: GlamourBrandRatingSummary | null },
  string,
  { rejectValue: string }
>(
  "glamourRatings/fetchBrandSummary",
  async (brandId, { rejectWithValue }) => {
    try {
      const snap = await getDoc(doc(db, "glamourBrandRatings", brandId));
      return {
        brandId,
        summary: snap.exists() ? (snap.data() as GlamourBrandRatingSummary) : null,
      };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch brand summary");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   FLAG / MODERATE A REVIEW  (admin)
═══════════════════════════════════════════════════════════ */

export const flagGlamourReview = createAsyncThunk<
  void,
  { listingId: string; guestId: string; orderId: string; flagReason: string },
  { rejectValue: string }
>(
  "glamourRatings/flagReview",
  async ({ listingId, guestId, orderId, flagReason }, { rejectWithValue }) => {
    try {
      const docRef = doc(db, COLLECTION, listingId, "reviews", guestId);
      const snap   = await getDoc(docRef);
      if (!snap.exists()) return;

      const reviews: GlamourProductReview[] = snap.data().reviews ?? [];
      const updated = reviews.map((r) =>
        r.orderId === orderId
          ? { ...r, isFlagged: true, flagReason, isApproved: false }
          : r
      );

      await updateDoc(docRef, { reviews: updated, lastUpdated: Timestamp.now() });
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to flag review");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   SLICE
═══════════════════════════════════════════════════════════ */

const glamourRatingSlice = createSlice({
  name: "glamourRatings",
  initialState,
  reducers: {
    clearRatings: (state) => {
      state.reviews        = {};
      state.summaries      = {};
      state.brandSummaries = {};
      state.reviewedOrders = {};
      state.error          = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      /* ── submit ── */
      .addCase(submitGlamourProductReview.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(submitGlamourProductReview.fulfilled, (state, action) => {
        const { listingId, review, summary } = action.payload;

        if (!state.reviews[listingId]) state.reviews[listingId] = [];
        // Avoid duplicates in local state
        const exists = state.reviews[listingId].some(
          (r) => r.orderId === review.orderId && r.guestId === review.guestId
        );
        if (!exists) state.reviews[listingId].unshift(review);

        state.summaries[listingId] = summary;
        state.reviewedOrders[`${listingId}_${review.orderId}`] = true;
        state.loading = false;
      })
      .addCase(submitGlamourProductReview.rejected, (state, action) => {
        state.error   = action.payload as string;
        state.loading = false;
      })

      /* ── fetchByListing ── */
      .addCase(fetchGlamourReviewsByListing.fulfilled, (state, action) => {
        state.reviews[action.payload.listingId] = action.payload.reviews;
        state.loading = false;
      })

      /* ── fetchByOrder ── */
      .addCase(fetchGlamourReviewByOrder.fulfilled, (state, action) => {
        if (action.meta.arg && action.payload) {
          const { listingId, orderId } = action.meta.arg;
          state.reviewedOrders[`${listingId}_${orderId}`] = true;
        }
        state.loading = false;
      })

      /* ── fetchListingSummary ── */
      .addCase(fetchGlamourListingSummary.fulfilled, (state, action) => {
        state.summaries[action.payload.listingId] = action.payload.summary;
        state.loading = false;
      })

      /* ── fetchAllSummaries ── */
      .addCase(fetchAllGlamourSummaries.fulfilled, (state, action) => {
        state.summaries = action.payload;
        state.loading   = false;
      })

      /* ── fetchBrandSummary ── */
      .addCase(fetchGlamourBrandSummary.fulfilled, (state, action) => {
        state.brandSummaries[action.payload.brandId] = action.payload.summary;
        state.loading = false;
      })

      /* ── global pending / rejected ── */
      .addMatcher(
        (a) => a.type.startsWith("glamourRatings/") && a.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error   = null;
        }
      )
      .addMatcher(
        (a) => a.type.startsWith("glamourRatings/") && a.type.endsWith("/rejected"),
        (state, a: any) => {
          state.loading = false;
          state.error   = a.payload ?? a.error?.message ?? "Something went wrong";
        }
      );
  },
});

export const { clearRatings, setError } = glamourRatingSlice.actions;

export default glamourRatingSlice.reducer;